// src/components/Layout.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  History as HistoryIcon,
  Users,
  MessagesSquare,
  FileText,
  Settings as SettingsIcon,
  AlertCircle,
  Shield,
  LogOut,
  Sparkles,
  ClipboardList,
  FileSearch,
  BarChart3,
  Layers,
  Globe,
  Building,
  ChevronDown,
  ChevronRight,
  CreditCard, // Billing (opcional)
  Wand2, // ⬅️ ícone para o Setup Wizard
} from 'lucide-react';

const STORAGE_KEY = 'sidebar.sections.open';

const Layout = ({ children }) => {
  const location = useLocation();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem('user') || '{}'),
    []
  );

  const isActive = (path) => {
    // Evita que "/" fique ativo em todas as rotas
    if (path === '/') {
      return location.pathname === '/';
    }
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + '/')
    );
  };

  const menu = [
    {
      title: 'Main',
      key: 'main',
      items: [
        // Dashboard agora em /dashboard, não mais "/"
        { icon: <Home size={16} />, label: 'Dashboard', path: '/dashboard' },
        { icon: <HistoryIcon size={16} />, label: 'History', path: '/history' },
        { icon: <MessagesSquare size={16} />, label: 'Awaiting Human', path: '/awaiting' },
      ],
    },
    {
      title: 'Client Interaction',
      key: 'client',
      items: [
        { icon: <MessagesSquare size={16} />, label: 'Predefined Messages', path: '/predefined' },
        { icon: <ClipboardList size={16} />, label: 'Client Notes', path: '/notes' },
        { icon: <FileText size={16} />, label: 'Voice Responses', path: '/voice' },
        { icon: <FileSearch size={16} />, label: 'Audio Transcriptions', path: '/transcriptions' },
      ],
    },
    {
      title: 'Management',
      key: 'management',
      items: [
        { icon: <Users size={16} />, label: 'Clients', path: '/clients' },
        { icon: <Users size={16} />, label: 'User Access', path: '/users' },
        { icon: <AlertCircle size={16} />, label: 'Urgent Alerts', path: '/alerts' },
        { icon: <ClipboardList size={16} />, label: 'Case Types', path: '/cases' },
        { icon: <Layers size={16} />, label: 'Message Scheduler', path: '/scheduler' },
        { icon: <Globe size={16} />, label: 'Alert Recipients', path: '/alert-recipients' },
      ],
    },
    {
      title: 'Analytics',
      key: 'analytics',
      items: [
        { icon: <BarChart3 size={16} />, label: 'Reports', path: '/reports' },
        { icon: <FileSearch size={16} />, label: 'IA Logs', path: '/logs' },
        { icon: <Sparkles size={16} />, label: 'Feedback', path: '/feedback' },
        { icon: <CreditCard size={16} />, label: 'Billing (Beta)', path: '/billing' },
      ],
    },
    {
      title: 'Moderation',
      key: 'moderation',
      items: [
        { icon: <Shield size={16} />, label: 'Blocked Numbers', path: '/blocked' },
        { icon: <Shield size={16} />, label: 'Word Filters', path: '/filters' },
      ],
    },
    {
      title: 'Setup',
      key: 'setup',
      items: [
        { icon: <Wand2 size={16} />, label: 'Setup Wizard', path: '/setup/wizard' },
        { icon: <Sparkles size={16} />, label: 'Informative Panel', path: '/setup/informative' },
        { icon: <Globe size={16} />, label: 'Phone Numbers', path: '/setup/phone-numbers' },
      ],
    },
    {
      title: 'Company',
      key: 'company',
      items: [
        { icon: <Building size={16} />, label: 'Profile', path: '/settings/profile' },
      ],
    },
  ];

  // ===== Estado das seções (colapsadas/abertas) com persistência =====
  const initialOpenState = useMemo(() => {
    // base: todas abertas
    const base = Object.fromEntries(menu.map((s) => [s.key, true]));
    // merge com localStorage
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        return { ...base, ...saved };
      }
    } catch {}
    return base;
  }, []); // eslint-disable-line

  const [open, setOpen] = useState(initialOpenState);

  // Abre automaticamente a seção que contém a rota ativa
  useEffect(() => {
    const sectionWithActive = menu.find((section) =>
      section.items.some((it) => isActive(it.path))
    );
    if (sectionWithActive && open[sectionWithActive.key] === false) {
      const next = { ...open, [sectionWithActive.key]: true };
      setOpen(next);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Persiste em localStorage sempre que open muda
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    } catch {}
  }, [open]);

  const toggleSection = (key) => {
    setOpen((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  return (
    <div className="flex min-h-screen bg-gray-100 text-gray-900">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white p-5 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">AIR Bot</h1>
        </div>

        {/* Seções */}
        <nav>
          {menu.map((section) => {
            const isOpen = open[section.key] !== false;
            return (
              <div key={section.key} className="mb-3">
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center justify-between text-left px-1 py-1.5"
                >
                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                    {section.title}
                  </h3>
                  {isOpen ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )}
                </button>

                {isOpen && (
                  <ul className="space-y-1 mt-1">
                    {section.items.map(({ label, path, icon }) => {
                      const active = isActive(path);
                      return (
                        <li key={path}>
                          <Link
                            to={path}
                            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                              active ? 'bg-gray-700 font-bold' : 'hover:bg-gray-700'
                            }`}
                          >
                            {icon}
                            {label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-800 pt-4">
          <button
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }}
            className="text-sm text-red-400 hover:underline"
          >
            <LogOut size={16} className="inline mr-2" /> Logout
          </button>
        </div>
      </aside>

      {/* Conteúdo */}
      <main className="flex-1 p-6 overflow-y-auto">{children}</main>
    </div>
  );
};

export default Layout;
